import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface CartItem {
  id: number;
  name: string;
  price: number;
  oldPrice?: number;
  image: string;
  color: string;
  quantity: number;
  category?: string;
}

interface CartContextValue {
  items: CartItem[];
  isOpen: boolean;
  totalCount: number;
  totalPrice: number;
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (id: number) => void;
  updateQty: (id: number, qty: number) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  hasItem: (id: number) => boolean;
}

interface CartProviderProps {
  children: ReactNode;
  onItemAdded?: (item: Omit<CartItem, 'quantity'>, isNew: boolean) => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export const CartProvider = ({ children, onItemAdded }: CartProviderProps) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const totalCount = items.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = items.reduce((s, i) => s + i.price * i.quantity, 0);

  const addItem = useCallback((item: Omit<CartItem, 'quantity'>) => {
    let isNew = false;
    setItems(prev => {
      const existing = prev.find(i => i.id === item.id);
      isNew = !existing;
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    setIsOpen(true);
    onItemAdded?.(item, isNew);
  }, [onItemAdded]);

  const removeItem = useCallback((id: number) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const updateQty = useCallback((id: number, qty: number) => {
    if (qty <= 0) {
      setItems(prev => prev.filter(i => i.id !== id));
    } else {
      setItems(prev => prev.map(i => i.id === id ? { ...i, quantity: qty } : i));
    }
  }, []);

  const clearCart = useCallback(() => setItems([]), []);
  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const hasItem = useCallback((id: number) => items.some(i => i.id === id), [items]);

  return (
    <CartContext.Provider value={{ items, isOpen, totalCount, totalPrice, addItem, removeItem, updateQty, clearCart, openCart, closeCart, hasItem }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
